import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Table, Button, Space, Tag, Popconfirm, Select, Spin, Typography, message } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import { newsAPI } from '../services/api';

const { Title } = Typography;
const { Option } = Select;

export default function News() {
    const [news, setNews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('');
    const [deleteLoading, setDeleteLoading] = useState(null);

    const fetchNews = useCallback(async () => {
        try {
            setLoading(true);
            const response = await newsAPI.getAll({ category: filter });
            setNews(Array.isArray(response.data) ? response.data : []);
        } catch (error) {
            console.error('Error fetching news:', error);
            message.error('Failed to fetch news');
            setNews([]);
        } finally {
            setLoading(false);
        }
    }, [filter]);

    useEffect(() => {
        fetchNews();
    }, [fetchNews]);

    const handleDelete = async (id) => {
        try {
            setDeleteLoading(id);
            await newsAPI.delete(id);
            setNews(news.filter((n) => (n.id || n._id) !== id));
            message.success('Article deleted successfully');
        } catch (error) {
            console.error('Error deleting news:', error);
            message.error('Failed to delete article');
        } finally {
            setDeleteLoading(null);
        }
    };

    const categoryLabels = {
        project: 'Project',
        news: 'News',
        technology: 'Technology',
        regulation: 'Regulation',
    };

    const categoryColors = {
        project: 'blue',
        news: 'green',
        technology: 'purple',
        regulation: 'orange',
    };

    const formatDate = (date) => {
        return new Date(date).toLocaleDateString('id-ID', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
    };

    const columns = [
        {
            title: 'Image',
            dataIndex: 'imageUrl',
            key: 'imageUrl',
            width: 100,
            render: (imageUrl) => (
                imageUrl ? (
                    <img
                        src={imageUrl.startsWith('http') ? imageUrl : `http://localhost:5000${imageUrl}`}
                        alt="Article thumbnail"
                        style={{
                            width: 60,
                            height: 60,
                            objectFit: 'cover',
                            borderRadius: 8
                        }}
                        onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="60" height="60"%3E%3Crect fill="%23ddd" width="60" height="60"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" fill="%23999" font-size="12"%3ENo Image%3C/text%3E%3C/svg%3E';
                        }}
                    />
                ) : (
                    <div style={{
                        width: 60,
                        height: 60,
                        backgroundColor: '#f0f0f0',
                        borderRadius: 8,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 10,
                        color: '#999'
                    }}>
                        No Image
                    </div>
                )
            ),
        },
        {
            title: 'Title',
            dataIndex: 'title',
            key: 'title',
            width: '30%',
        },
        {
            title: 'Category',
            dataIndex: 'category',
            key: 'category',
            render: (category) => (
                <Tag color={categoryColors[category] || 'default'}>
                    {categoryLabels[category] || category}
                </Tag>
            ),
        },
        {
            title: 'Published Date',
            dataIndex: 'publishedDate',
            key: 'publishedDate',
            render: (date) => formatDate(date),
        },
        {
            title: 'Status',
            dataIndex: 'isPublished',
            key: 'isPublished',
            render: (isPublished) => (
                <Tag color={isPublished ? 'success' : 'default'}>
                    {isPublished ? 'Published' : 'Draft'}
                </Tag>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 200,
            render: (_, record) => (
                <Space>
                    <Button
                        type="link"
                        icon={<EyeOutlined />}
                        size="small"
                    >
                        View
                    </Button>
                    <Link to={`/news/edit/${record.id || record._id}`}>
                        <Button
                            type="primary"
                            icon={<EditOutlined />}
                            size="small"
                        >
                            Edit
                        </Button>
                    </Link>
                    <Popconfirm
                        title="Are you sure you want to delete this article?"
                        onConfirm={() => handleDelete(record.id || record._id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button
                            danger
                            icon={<DeleteOutlined />}
                            size="small"
                            loading={deleteLoading === (record.id || record._id)}
                        >
                            Delete
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: '50px' }}>
                <Spin size="large" tip="Loading articles..." />
            </div>
        );
    }

    return (
        <div>
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Title level={2}>Articles</Title>
                <Link to="/news/new">
                    <Button type="primary" icon={<PlusOutlined />} size="large">
                        Add New Article
                    </Button>
                </Link>
            </div>

            <div style={{ marginBottom: 16 }}>
                <Space>
                    <span style={{ fontWeight: 500 }}>Filter by Category:</span>
                    <Select
                        value={filter}
                        onChange={(value) => setFilter(value)}
                        style={{ width: 200 }}
                        placeholder="All Categories"
                    >
                        <Option value="">All Categories</Option>
                        <Option value="project">Project</Option>
                        <Option value="news">News</Option>
                        <Option value="technology">Technology</Option>
                        <Option value="regulation">Regulation</Option>
                    </Select>
                </Space>
            </div>

            <Table
                columns={columns}
                dataSource={news}
                rowKey={(record) => record.id || record._id}
                pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                    showTotal: (total) => `Total ${total} articles`,
                }}
                locale={{
                    emptyText: 'No articles found',
                }}
            />
        </div>
    );
}
